/**
 * API rows -> the shapes the tabs already use (WorkRow, CallRow, ...). Pure functions, no state.
 *
 * Rules kept here, once:
 *  - Work with no agent is shown as "Unassigned": it is the backlog, and hiding it would flatter everyone.
 *  - Orders with no valid agent tag are NOT agent work: they leave the rows and are reported as one footnote figure.
 *  - An order with neither a tag nor open work stays visible as "No open work".
 */
import type { ApiCallRow, ApiCategories, ApiOrderRow, ApiSummary, ApiWorkRow, WithHour } from './api';
import type {
  AutomationCounts, CallRow, CatalogAgent, CatalogCategory, CatalogMetrics, CatalogProduct, OrderBucketKey, StageKey, StageSplit, TrendPoint, WorkRow,
} from './types';

export const UNASSIGNED = 'Unassigned';
export const UNMATCHED = 'Unmatched agent';
const BUCKET_KEYS = ['abc', 'reorder', 'rto', 'edd', 'ndr', 'agent_lead', 'phone'];
const asBucket = (b: string | null): OrderBucketKey => (b && BUCKET_KEYS.includes(b) ? (b as OrderBucketKey) : 'none');

export interface HourPoint { hour: string; dials: number; connected: number }
export interface AttemptMix { customers: number; a1: number; a2: number; a3: number; a4plus: number; never: number }
export interface NoAgentOrders { orders: number; value: number }

export interface Adapted {
  rows: WorkRow[]; calls: CallRow[]; agents: string[]; agentIdByName: Record<string, number>;
  automation: AutomationCounts; noAgent: NoAgentOrders; trend: TrendPoint[]; hourly: HourPoint[]; attempts: AttemptMix;
  notInConnectivity: { inbound: number; dialerClicks: number; aiCalls: number };
  uniqueCustomers: number; customersByBucket: Record<string, number>; coverage: Record<string, string | null>; asOf: string; ownOnly: boolean;
  minTalkSeconds: number; lockDays: number;
}

export const emptyStages = (): Record<StageKey, StageSplit> => ({
  delivered: { orders: 0, value: 0 }, inTransit: { orders: 0, value: 0 }, awaitingPickup: { orders: 0, value: 0 },
  returned: { orders: 0, value: 0 }, cancelled: { orders: 0, value: 0 },
});

const blankRow = (agent: string, bucket: OrderBucketKey): WorkRow => ({
  agent, bucket, received: 0, workItems: 0, uniqueCustomers: 0, uniqueOrders: null, worked: 0, workedManual: 0,
  workedTata: 0, workedUnsplit: 0, closedByAgent: 0, closedBySystem: 0, orders: 0, validOrders: 0, cancelledOrders: 0, units: 0,
  validRevenue: 0, cancelledValue: 0, tagOrders: 0, couponOrders: 0, booked: 0, stages: emptyStages(), tag: emptyStages(),
});

function fillWork(r: WorkRow, w: ApiWorkRow): void {
  r.received = w.received; r.workItems = w.work_items; r.uniqueCustomers = w.unique_customers;
  r.uniqueOrders = w.unique_orders; r.worked = w.worked; r.workedManual = w.worked_manual; r.workedTata = w.worked_tata;
  r.workedUnsplit = w.worked_unsplit; r.closedByAgent = w.closed_by_agent; r.closedBySystem = w.closed_by_system;
}

function addOrder(r: WorkRow, o: ApiOrderRow): void {
  r.orders += o.orders; r.validOrders += o.valid_orders; r.cancelledOrders += o.cancelled_orders; r.units += o.units;
  r.validRevenue += o.valid_revenue; r.cancelledValue += o.cancelled_value; r.tagOrders += o.tag_orders; r.couponOrders += o.coupon_orders;
  r.booked += o.booked ?? 0;
  r.stages.delivered.orders += o.st_delivered_orders ?? 0; r.stages.delivered.value += o.st_delivered_value ?? 0;
  r.stages.inTransit.orders += o.st_in_transit_orders ?? 0; r.stages.inTransit.value += o.st_in_transit_value ?? 0;
  r.stages.awaitingPickup.orders += o.st_awaiting_pickup_orders ?? 0; r.stages.awaitingPickup.value += o.st_awaiting_pickup_value ?? 0;
  r.stages.returned.orders += o.st_returned_orders ?? 0; r.stages.returned.value += o.st_returned_value ?? 0;
  r.stages.cancelled.orders += o.st_cancelled_orders ?? 0; r.stages.cancelled.value += o.st_cancelled_value ?? 0;
  const tg = r.tag;
  tg.delivered.orders += o.tg_delivered_orders ?? 0; tg.delivered.value += o.tg_delivered_value ?? 0;
  tg.inTransit.orders += o.tg_in_transit_orders ?? 0; tg.inTransit.value += o.tg_in_transit_value ?? 0;
  tg.awaitingPickup.orders += o.tg_awaiting_pickup_orders ?? 0; tg.awaitingPickup.value += o.tg_awaiting_pickup_value ?? 0;
  tg.returned.orders += o.tg_returned_orders ?? 0; tg.returned.value += o.tg_returned_value ?? 0;
  tg.cancelled.orders += o.tg_cancelled_orders ?? 0; tg.cancelled.value += o.tg_cancelled_value ?? 0;
}

const callRow = (agent: string, c: ApiCallRow): CallRow => ({
  agent, bucket: asBucket(c.bucket), dials: c.dials, connected: c.connected,
  noAnswer: c.no_answer, cancelledRinging: c.cancelled_ringing, droppedOther: c.dropped_other, hungUpEarly: c.hung_up_early,
  talkSeconds: c.talk_seconds, shortCalls: c.short_calls, ringSeconds: c.ring_seconds,
  customersCalled: c.customers_called, customersConnected: c.customers_connected, activeDays: c.active_days,
});

/** One bucket's rows by hour of day, earliest first. `row` has the same shape the tab already reads for a bucket. */
export type HourlyByBucket<T> = Record<string, { hour: number; row: T }[]>;

function byBucket<A extends { bucket: string | null; hour: number }, T>(rows: A[], make: (r: A) => T): HourlyByBucket<T> {
  const out: HourlyByBucket<T> = {};
  rows.forEach(r => { (out[asBucket(r.bucket)] ??= []).push({ hour: r.hour, row: make(r) }); });
  Object.values(out).forEach(list => list.sort((x, y) => x.hour - y.hour));
  return out;
}

const ALL_AGENTS = 'All agents';
export const adaptHourlyWork = (rows: WithHour<ApiWorkRow>[]): HourlyByBucket<WorkRow> =>
  byBucket(rows, w => { const r = blankRow(ALL_AGENTS, asBucket(w.bucket)); fillWork(r, w); return r; });
export const adaptHourlyOrders = (rows: WithHour<ApiOrderRow>[]): HourlyByBucket<WorkRow> =>
  byBucket(rows, o => { const r = blankRow(ALL_AGENTS, asBucket(o.bucket)); addOrder(r, o); return r; });
export const adaptHourlyCalls = (rows: WithHour<ApiCallRow>[]): HourlyByBucket<CallRow> =>
  byBucket(rows, c => callRow(ALL_AGENTS, c));

export function hourLabel(h: number): string {
  return `${h % 12 === 0 ? 12 : h % 12} ${h >= 12 ? 'pm' : 'am'}`;
}

export function adaptSummary(s: ApiSummary): Adapted {
  const names = s.agents;
  const nameOf = (id: number | null, missing = UNASSIGNED) => (id == null ? missing : names[String(id)] ?? `User ${id}`);
  const map = new Map<string, WorkRow>();
  const row = (agent: string, bucket: OrderBucketKey) => {
    const k = `${agent}|${bucket}`;
    let r = map.get(k);
    if (!r) { r = blankRow(agent, bucket); map.set(k, r); }
    return r;
  };

  s.work.forEach((w: ApiWorkRow) => {
    fillWork(row(nameOf(w.agent_id), asBucket(w.bucket)), w);
  });
  s.orders.forEach((o: ApiOrderRow) => {
    if (o.agent_id == null) return;                 // not agent work: counted in `noAgent`
    addOrder(row(nameOf(o.agent_id), asBucket(o.bucket)), o);
  });

  const calls: CallRow[] = s.calls.map((c: ApiCallRow) => callRow(nameOf(c.agent_id, UNMATCHED), c));

  const agentIdByName: Record<string, number> = {};
  Object.entries(names).forEach(([id, n]) => { agentIdByName[n] = Number(id); });
  const rows = Array.from(map.values());
  const agentNames = Array.from(new Set([...rows.map(r => r.agent), ...calls.map(c => c.agent)]))
    .filter(n => n !== UNASSIGNED && n !== UNMATCHED).sort((a, b) => a.localeCompare(b));

  const ex = s.call_extras;
  const na = s.no_agent_orders;
  return {
    rows, calls, agents: agentNames, agentIdByName,
    automation: { engatiAi: s.automation?.engati_ai ?? 0, abcAutoUpdated: s.automation?.abc_auto_updated ?? 0, otherSystem: s.automation?.other_system ?? 0 },
    noAgent: { orders: na?.orders ?? 0, value: na?.valid_revenue ?? 0 },
    trend: s.orders_over_time.map(p => ({ day: new Date(p.period + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), valid: p.valid, cancelled: p.cancelled })),
    hourly: ex.hourly.map(h => ({ hour: hourLabel(h.hour), dials: h.dials, connected: h.connected })),
    attempts: ex.attempts,
    notInConnectivity: { inbound: ex.not_in_connectivity.inbound, dialerClicks: ex.not_in_connectivity.dialer_clicks, aiCalls: ex.not_in_connectivity.ai_calls },
    uniqueCustomers: s.unique_customers, customersByBucket: s.unique_customers_by_bucket ?? {}, coverage: s.coverage, asOf: s.data_as_of, ownOnly: s.own_only,
    minTalkSeconds: s.definitions.min_talk_seconds, lockDays: s.definitions.attribution_lock_days,
  };
}

const ZERO: CatalogMetrics = { orders: 0, valid: 0, cancelled: 0, units: 0, revenue: 0, cancelledValue: 0, workItems: 0, booked: 0, stages: emptyStages() };
export const addStages = (a: Record<StageKey, StageSplit>, b: Record<StageKey, StageSplit>): Record<StageKey, StageSplit> => {
  const out = emptyStages();
  (Object.keys(out) as StageKey[]).forEach(k => { out[k] = { orders: a[k].orders + b[k].orders, value: a[k].value + b[k].value }; });
  return out;
};
const addM = (a: CatalogMetrics, b: CatalogMetrics): CatalogMetrics => ({
  orders: a.orders + b.orders, valid: a.valid + b.valid, cancelled: a.cancelled + b.cancelled, units: a.units + b.units,
  revenue: a.revenue + b.revenue, cancelledValue: a.cancelledValue + b.cancelledValue, workItems: a.workItems + b.workItems,
  booked: a.booked + b.booked, stages: addStages(a.stages, b.stages),
});

/** Category > product > agent tree, largest revenue first. Orders with no agent tag appear as "No agent tag". */
export function adaptCategories(c: ApiCategories): CatalogCategory[] {
  const names = c.agents;
  const cats = new Map<string, Map<string, CatalogAgent[]>>();
  c.rows.forEach(r => {
    const a: CatalogAgent = {
      agent: r.agent_id == null ? 'No agent tag' : names[String(r.agent_id)] ?? `User ${r.agent_id}`,
      orders: r.orders, valid: r.valid_orders, cancelled: r.cancelled_orders, units: r.units, revenue: r.valid_revenue,
      cancelledValue: r.cancelled_value, workItems: 0, booked: r.booked ?? 0,
      stages: {
        delivered: { orders: r.st_delivered_orders ?? 0, value: r.st_delivered_value ?? 0 },
        inTransit: { orders: r.st_in_transit_orders ?? 0, value: r.st_in_transit_value ?? 0 },
        awaitingPickup: { orders: r.st_awaiting_pickup_orders ?? 0, value: r.st_awaiting_pickup_value ?? 0 },
        returned: { orders: r.st_returned_orders ?? 0, value: r.st_returned_value ?? 0 },
        cancelled: { orders: r.st_cancelled_orders ?? 0, value: r.st_cancelled_value ?? 0 },
      },
    };
    if (!cats.has(r.category)) cats.set(r.category, new Map());
    const prods = cats.get(r.category)!;
    prods.set(r.product, [...(prods.get(r.product) ?? []), a]);
  });
  const out: CatalogCategory[] = [];
  cats.forEach((prods, category) => {
    const products: CatalogProduct[] = Array.from(prods.entries()).map(([product, agents]) => {
      const m = agents.reduce<CatalogMetrics>((acc, a) => addM(acc, a), ZERO);
      return { product, ...m, agents: agents.sort((x, y) => y.revenue - x.revenue) };
    }).sort((x, y) => y.revenue - x.revenue);
    out.push({ category, ...products.reduce<CatalogMetrics>((acc, p) => addM(acc, p), ZERO), products });
  });
  return out.sort((x, y) => y.revenue - x.revenue);
}
